import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable } from 'rxjs';
/**
 * Intercepts outgoing Angular HTTP requests and conditionally adds an impersonation header.
 *
 * This interceptor reads an impersonated employee identifier from sessionStorage under the key
 * 'IMPERSONATE_EMPLOYEE_ID'. If present, it appends the custom header:
 *
 *   X-Impersonate-Employee-Id: <employeeId>
 *
 * The header is NOT added when the request URL contains any substring defined in the internal
 * skip list (currently: "managers/subordinates"), allowing certain endpoints to always execute
 * in the context of the real (non‑impersonated) user.
 *
 * Behavior summary:
 * - If no impersonation ID is stored: passes the original request unchanged.
 * - If an impersonation ID exists AND the URL is not in the skip list: clones the request
 *   and adds the header.
 * - Skip list checks are case-insensitive.
 *
 * Use cases:
 * - Support administrative "act as user" flows.
 * - Audit or debugging scenarios where backend must know the effective acting user.
 *
 * Storage contract:
 * - The impersonation value is retrieved using sessionStorage.getItem('IMPERSONATE_EMPLOYEE_ID').
 * - Clearing that key (or the entire session storage) disables impersonation automatically.
 *
 * Security considerations:
 * - Ensure the backend validates that the caller is authorized to impersonate the target employee.
 * - Do not rely solely on the presence of the header for trust decisions.
 *
 * Performance:
 * - Adds negligible overhead (string lowercase + substring checks + optional clone).
 *
 * Extensibility suggestions:
 * - Externalize the skip list to configuration if more routes are added.
 * - Migrate storage access to a dedicated service for easier testing.
 * - Consider supporting a parallel "X-Impersonated-By" header if required for auditing.
 *
 * Example (conceptual):
 * const id = '12345';
 * sessionStorage.setItem('IMPERSONATE_EMPLOYEE_ID', id);
 * // Subsequent HTTP calls (except skipped URLs) include:
 * //   X-Impersonate-Employee-Id: 12345
 *
 * @remarks
 * Place this interceptor in the HTTP_INTERCEPTORS multi-provider list:
 *   { provide: HTTP_INTERCEPTORS, useClass: ImpersonateEmployeeInterceptor, multi: true }
 *
 * @see HttpInterceptor
 * @see HttpRequest
 * @see HttpHandler
 */
@Injectable()
export class ImpersonateEmployeeInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const impersonateEmployeeId = this.getImpersonateEmployeeId();

    const skipImpersonationSubstrings: string[] = ['managers/subordinates'];

    const lowerUrl = req.url.toLowerCase();
    const shouldSkipImpersonation = skipImpersonationSubstrings.some((sub) =>
      lowerUrl.includes(sub.toLowerCase()),
    );

    if (impersonateEmployeeId && !shouldSkipImpersonation) {
      const modifiedReq = req.clone({
        setHeaders: {
          'X-Impersonate-Employee-Id': impersonateEmployeeId,
        },
      });
      return next.handle(modifiedReq);
    }

    return next.handle(req);
  }

  private getImpersonateEmployeeId(): string | null {
    return sessionStorage.getItem('IMPERSONATE_EMPLOYEE_ID');
  }
}
